// Centralized date/time formatting for the UI.
// Target display style: "5 Sept 2026, 8:59 pm"

// Month abbreviations matching the desired style (note: "Sept" for September).
const MONTHS_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sept", "Oct", "Nov", "Dec",
];

// Parse a value into a Date. Handles Date objects, ISO strings, and
// plain "YYYY-MM-DD" dates (treated as local midnight to avoid TZ shifts).
const toDate = (value) => {
  if (value == null || value === "") return null;
  if (value instanceof Date) return isNaN(value) ? null : value;
  if (typeof value === "string") {
    // Date-only string -> anchor to local midnight
    const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value.trim());
    const d = new Date(dateOnly ? `${value.trim()}T00:00:00` : value);
    return isNaN(d) ? null : d;
  }
  const d = new Date(value);
  return isNaN(d) ? null : d;
};

const timePart = (d) => {
  let h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? "pm" : "am";
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${String(m).padStart(2, "0")} ${ampm}`;
};

// Ordinal suffix for a day of month: 1 -> "1st", 2 -> "2nd", 11 -> "11th", 23 -> "23rd".
const ordinal = (n) => {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
};

// "7th Sept 2026"
export const formatDate = (value, fallback = "—") => {
  const d = toDate(value);
  if (!d) return fallback;
  return `${ordinal(d.getDate())} ${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`;
};

// "5 Sept 2026, 8:59 pm"
export const formatDateTime = (value, fallback = "—") => {
  const d = toDate(value);
  if (!d) return fallback;
  return `${formatDate(d)}, ${timePart(d)}`;
};

// "8:59 pm"
export const formatTime = (value, fallback = "—") => {
  const d = toDate(value);
  if (!d) return fallback;
  return timePart(d);
};

// Convert an "HH:mm[:ss]" time string to "8:59 pm" (no date component).
export const formatClock = (hhmm, fallback = "—") => {
  if (!hhmm) return fallback;
  const [h, m] = String(hhmm).split(":").map(Number);
  if (isNaN(h)) return fallback;
  const ampm = h >= 12 ? "pm" : "am";
  const hr = h % 12 === 0 ? 12 : h % 12;
  return `${hr}:${String(isNaN(m) ? 0 : m).padStart(2, "0")} ${ampm}`;
};
